import React from "react";
import {renderDashedLine, renderLine} from "../../renderFunctions";
import {getBevelHeight, getLidTopToBevelTopDistance, hasMultiRowManholes} from "../../../../domain/lid";
import {Group} from "react-konva";
import {createManholeGeometry} from "./manholeGeometry";
import {getClosuresIntersectionPositions, getClosureSizes} from "../../../../domain/manhole";
import {LidGeometry} from "./lidGeometry";
import {Plan, Manhole, Side} from "types"


interface ConventionalManholeProps {
    plan: Plan;
    lidGeometry: LidGeometry;
    scale: number;
    manhole: Manhole;
    side: Side;
}


export const ConventionalManhole: React.FC<ConventionalManholeProps> = ({plan, lidGeometry, scale, manhole, side}) => {
    const manholeGeometry = createManholeGeometry(plan, manhole, scale, side, lidGeometry)

    const isClosuresShortSide = () => {
        return (
            ((side === 'front' || side === 'back') && manhole.orientation === 'vertical')
            || ((side === 'left' || side === 'right') && manhole.orientation === 'horizontal')
        )
    }
    const isClosuresLongSide = () => !isClosuresShortSide()
    const getClosuresTop = () => lidGeometry.top
    const getClosuresThickness = () => manholeGeometry.closureThickness()
    const getClosuresBottom = () => getClosuresTop() + getClosuresThickness()
    const sideHasRabbet = () => {
        return side === 'front' || side === 'back'
            ? manhole.orientation === 'horizontal'
            : manhole.orientation === 'vertical'
    }
    const getRabbetWidth = () => manholeGeometry.rabbetWidth()
    const getManholeLeft = () => manholeGeometry.clearLeft() - (sideHasRabbet() ? manholeGeometry.rabbetWidth() : 0)
    const getManholeRight = () => manholeGeometry.clearRight() + (sideHasRabbet() ? manholeGeometry.rabbetWidth() : 0)
    const renderWithCollar = () => {
        const renderBeveledOutline = () => {
            const getBevelTop = () => getLidTopToBevelTopDistance(plan) * scale
            const getBevelBottom = () => getBevelTop() + getBevelHeight(plan) * scale

            return (
                <React.Fragment>
                    {
                        renderLine([
                            getManholeLeft(), lidGeometry.top,
                            getManholeLeft(), getBevelTop(),
                            lidGeometry.left, getBevelBottom(),
                            lidGeometry.left, lidGeometry.bottom,
                            lidGeometry.right, lidGeometry.bottom,
                            lidGeometry.right, getBevelBottom(),
                            getManholeRight(), getBevelTop(),
                            getManholeRight(), lidGeometry.top,
                            getManholeLeft(), lidGeometry.top
                        ])
                    }
                    {renderLine([getManholeLeft(), getBevelTop(), getManholeRight(), getBevelTop()])}
                    {renderLine([lidGeometry.left, getBevelBottom(), lidGeometry.right, getBevelBottom()])}
                </React.Fragment>
            )
        }
        const renderClosuresLongSide = () => {
            const innerLeft = manholeGeometry.clearLeft()
            const realInnerWidth = (manhole.orientation === 'vertical' ? manhole.height : manhole.width)
            const innerWidth = realInnerWidth * scale
            const innerRight = innerLeft + innerWidth
            const outerLeft = innerLeft - getRabbetWidth()
            const outerRight = innerRight + getRabbetWidth()

            const renderGirder = (x: number, i: number) => {
                const topAndBottomBarWidth = 2
                const middleBarWidth = 1
                const top = 0
                const bottom = 18 * scale
                const left = -9 * scale
                const right = 9 * scale
                const topBarBottom = top + topAndBottomBarWidth * scale
                const bottomBarTop = bottom - topAndBottomBarWidth * scale
                const middleBarLeft = -middleBarWidth / 2 * scale
                const middleBarRight = middleBarWidth / 2 * scale

                return (
                    <Group x={x} y={getClosuresBottom()}>
                        {renderDashedLine([
                            left, top,
                            left, topBarBottom,
                            middleBarLeft, topBarBottom,
                            middleBarLeft, bottomBarTop,
                            left, bottomBarTop,
                            left, bottom,
                            right, bottom,
                            right, bottomBarTop,
                            middleBarRight, bottomBarTop,
                            middleBarRight, topBarBottom,
                            right, topBarBottom,
                            right, top
                        ], 'girder_' + i)}
                    </Group>
                )
            }

            return (
                <React.Fragment>
                    {renderDashedLine(
                        [
                            outerLeft,
                            lidGeometry.top,
                            outerLeft,
                            getClosuresBottom(),
                            outerRight,
                            getClosuresBottom(),
                            outerRight,
                            lidGeometry.top
                        ]
                    )}
                    {renderDashedLine([innerLeft, getClosuresBottom(), innerLeft, lidGeometry.bottom])}
                    {renderDashedLine([innerRight, getClosuresBottom(), innerRight, lidGeometry.bottom])}
                    {
                        getClosuresIntersectionPositions(
                            realInnerWidth + 7,
                            getClosureSizes(manhole.type).height + getClosureSizes(manhole.type).rowSpacing
                        )
                            .map(p => outerLeft + p * scale)
                            .map((p, i) => (
                                <React.Fragment key={"intersection_" + i}>
                                    {renderDashedLine([p, lidGeometry.top, p, getClosuresBottom()])}
                                    {renderGirder(p, i)}
                                </React.Fragment>
                            ))
                    }
                </React.Fragment>
            )
        }
        const renderClosuresShortSide = () => {
            const left = manholeGeometry.clearLeft()
            const realWidth = manhole.orientation === 'vertical' ? manhole.width : manhole.height
            const width = realWidth * scale
            const right = left + width

            const renderGirder = () => {
                const topAndBottomBarWidth = 2
                const top = 0
                const bottom = 18 * scale
                const topBarBottom = top + topAndBottomBarWidth * scale
                const bottomBarTop = bottom - topAndBottomBarWidth * scale

                return (
                    <Group x={left} y={getClosuresBottom()}>
                        {renderDashedLine([0, topBarBottom, width, topBarBottom])}
                        {renderDashedLine([0, bottomBarTop, width, bottomBarTop])}
                        {renderDashedLine([0, bottom, width, bottom])}
                    </Group>
                )
            }

            return (
                <React.Fragment>
                    {renderDashedLine([left, lidGeometry.top, left, lidGeometry.bottom])}
                    {renderDashedLine([right, lidGeometry.top, right, lidGeometry.bottom])}
                    {renderDashedLine([left, getClosuresBottom(), right, getClosuresBottom()])}
                    {
                        getClosuresIntersectionPositions(realWidth, getClosureSizes(manhole.type).width)
                            .map(p => left + p * scale)
                            .map((p, i) =>
                                renderDashedLine([p, lidGeometry.top, p, getClosuresBottom()], 'divider_' + i)
                            )
                    }
                    {hasMultiRowManholes(plan) && renderGirder()}
                </React.Fragment>
            )
        }

        return (
            <React.Fragment>
                {renderBeveledOutline()}
                {isClosuresShortSide() && renderClosuresShortSide()}
                {isClosuresLongSide() && renderClosuresLongSide()}
            </React.Fragment>
        )
    }
    const renderWithoutCollar = () => {
        const renderA15 = () => {
            const getFrameWallWidth = () => 0.5 * scale
            const renderClosuresShortSide = () => {
                const renderLidOutline = () => {
                    return renderLine([
                        getManholeLeft() - getFrameWallWidth(), lidGeometry.top,
                        getManholeLeft() - getFrameWallWidth(), lidGeometry.top + 8 * scale,
                        getManholeLeft() - 5.5 * scale, lidGeometry.top + 8 * scale,
                        getManholeLeft() - 5.5 * scale, lidGeometry.top + 8.5 * scale,
                        getManholeRight() + 5.5 * scale, lidGeometry.top + 8.5 * scale,
                        getManholeRight() + 5.5 * scale, lidGeometry.top + 8 * scale,
                        getManholeRight() + getFrameWallWidth(), lidGeometry.top + 8 * scale,
                        getManholeRight() + getFrameWallWidth(), lidGeometry.top,
                        getManholeLeft() - getFrameWallWidth(), lidGeometry.top
                    ])
                }
                const renderClosuresOutline = () => {
                    return renderDashedLine([
                        getManholeLeft(), lidGeometry.top,
                        getManholeLeft(), lidGeometry.top + getClosureSizes(manhole.type).thickness * scale,
                        getManholeRight(), lidGeometry.top + getClosureSizes(manhole.type).thickness * scale,
                        getManholeRight(), lidGeometry.top
                    ])
                }
                const renderClosuresIntersectionLines = () => {
                    return (
                        <React.Fragment>
                            {
                                manholeGeometry.closureIntersectionPositions()
                                    .map((p, i) => renderDashedLine(
                                        [p, lidGeometry.top, p, getClosuresBottom()],
                                        'closures_intersection_' + i
                                    ))
                            }
                        </React.Fragment>
                    )

                }
                const renderAnchors = () => {
                    const getClosureWidth = () => getClosureSizes(manhole.type).width
                    const getClosuresCount = () => Math.round(manholeGeometry.physicalClearWidth() / getClosureWidth())
                    const getAnchorXPositionCount = () => Math.ceil(getClosuresCount() / 2)
                    const getAnchorXPositionsDistance =
                        () => manholeGeometry.physicalClearWidth() / getAnchorXPositionCount() * scale
                    const getAnchorXPositions = () => {
                        return Array(getAnchorXPositionCount()).fill(null).map((_curr, i) => {
                            return (0.5 + i) * getAnchorXPositionsDistance() + getManholeLeft()
                        })
                    }
                    const getFrameBottom = () => lidGeometry.top + 8.5 * scale

                    return getAnchorXPositions().map(
                        (p, i) => {
                            return (
                                <React.Fragment key={'lid_elevation_view_anchor_' + i}>
                                    {
                                        renderLine([
                                            p - 1.5 * scale, getFrameBottom(),
                                            p - 1.5 * scale, getFrameBottom() + 9 * scale,
                                            p + 1.5 * scale, getFrameBottom() + 9 * scale,
                                            p + 1.5 * scale, getFrameBottom()
                                        ])
                                    }
                                    {
                                        renderLine([
                                            p, getFrameBottom() + 6 * scale,
                                            p, getFrameBottom() + 9 * scale
                                        ])
                                    }
                                </React.Fragment>
                            )
                        }
                    )
                }

                return (
                    <React.Fragment>
                        {renderLidOutline()}
                        {renderClosuresOutline()}
                        {renderClosuresIntersectionLines()}
                        {renderAnchors()}
                    </React.Fragment>
                )
            }
            const renderClosuresLongSide = () => {
                const renderLidOutline = () => {
                    return renderLine([
                        getManholeLeft() - getFrameWallWidth(), lidGeometry.top,
                        getManholeLeft() - getFrameWallWidth(), lidGeometry.top + 8.5 * scale,
                        getManholeRight() + getFrameWallWidth(), lidGeometry.top + 8.5 * scale,
                        getManholeRight() + getFrameWallWidth(), lidGeometry.top,
                        getManholeLeft() - getFrameWallWidth(), lidGeometry.top
                    ])
                }
                const renderFrameLine = () => {
                    return renderLine([
                        getManholeLeft() - getFrameWallWidth(), lidGeometry.top + 8 * scale,
                        getManholeRight() + getFrameWallWidth(), lidGeometry.top + 8 * scale
                    ])
                }
                const renderClosureLines = () => {
                    return (
                        <React.Fragment>
                            {renderDashedLine([
                                getManholeLeft(), lidGeometry.top,
                                getManholeLeft(), lidGeometry.top + 8 * scale
                            ])}
                            {renderDashedLine([
                                getManholeRight(), lidGeometry.top,
                                getManholeRight(), lidGeometry.top + 8 * scale
                            ])}
                        </React.Fragment>
                    )
                }
                const renderAnchors = () => {
                    const renderAnchor = () => {
                        return (
                            <React.Fragment>
                                {
                                    renderLine([
                                        0.25 * scale, 0,
                                        0.25 * scale, 6 * scale,
                                        2.25 * scale, 8.7 * scale,
                                        1.85 * scale, 9 * scale,
                                        -0.15 * scale, 6.3 * scale,
                                        -0.25 * scale, 6 * scale,
                                        -0.25 * scale, 0
                                    ])
                                }
                                {
                                    renderLine([
                                        -0.25 * scale, 6 * scale,
                                        -2.25 * scale, 8.7 * scale,
                                        -1.85 * scale, 9 * scale,
                                        0, 6.5 * scale
                                    ])
                                }
                            </React.Fragment>
                        )
                    }
                    const getFrameBottom = () => lidGeometry.top + 8.5 * scale

                    return (
                        <React.Fragment>
                            <Group x={getManholeLeft() - 0.25 * scale} y={getFrameBottom()}>
                                {renderAnchor()}
                            </Group>
                            <Group x={getManholeRight() + 0.25 * scale} y={getFrameBottom()}>
                                {renderAnchor()}
                            </Group>
                        </React.Fragment>
                    )
                }

                return (
                    <React.Fragment>
                        {renderLidOutline()}
                        {renderFrameLine()}
                        {renderClosureLines()}
                        {renderAnchors()}
                    </React.Fragment>
                )
            }

            return (
                <React.Fragment>
                    {isClosuresShortSide() && renderClosuresShortSide()}
                    {isClosuresLongSide() && renderClosuresLongSide()}
                </React.Fragment>
            )
        }
        const renderB125 = () => {
            const getFrameWallWidth = () => 0.5 * scale
            const renderClosuresShortSide = () => {
                const renderLidOutline = () => {
                    return renderLine([
                        getManholeLeft() - getFrameWallWidth(), lidGeometry.top,
                        getManholeLeft() - getFrameWallWidth(), lidGeometry.top + 7.5 * scale,
                        getManholeLeft() - 4 * scale, lidGeometry.top + 7.5 * scale,
                        getManholeLeft() - 4 * scale, lidGeometry.top + 8 * scale,
                        getManholeRight() + 4 * scale, lidGeometry.top + 8 * scale,
                        getManholeRight() + 4 * scale, lidGeometry.top + 7.5 * scale,
                        getManholeRight() + getFrameWallWidth(), lidGeometry.top + 7.5 * scale,
                        getManholeRight() + getFrameWallWidth(), lidGeometry.top,
                        getManholeLeft() - getFrameWallWidth(), lidGeometry.top
                    ])
                }
                const renderClosuresOutline = () => {
                    return renderDashedLine([
                        getManholeLeft(), lidGeometry.top,
                        getManholeLeft(), lidGeometry.top + getClosureSizes(manhole.type).thickness * scale,
                        getManholeRight(), lidGeometry.top + getClosureSizes(manhole.type).thickness * scale,
                        getManholeRight(), lidGeometry.top
                    ])
                }
                const renderClosuresIntersectionLines = () => {
                    return (
                        <React.Fragment>
                            {
                                manholeGeometry.closureIntersectionPositions()
                                    .map((p, i) => renderDashedLine(
                                        [p, lidGeometry.top, p, getClosuresBottom()],
                                        'closures_intersection_' + i
                                    ))
                            }
                        </React.Fragment>
                    )
                }
                const renderAnchors = () => {
                    const getClosureWidth = () => getClosureSizes(manhole.type).width
                    const getClosuresCount = () => Math.round(manholeGeometry.physicalClearWidth() / getClosureWidth())
                    const getAnchorXPositions = () => {
                        return Array(getClosuresCount()).fill(null).map((_curr, i) => {
                            return (0.5 + i) * getClosureWidth() * scale + getManholeLeft()
                        })
                    }
                    const getFrameBottom = () => lidGeometry.top + 8 * scale

                    return getAnchorXPositions().map(
                        p => {
                            return (
                                <React.Fragment key={'anchor_x_pos_' + p}>
                                    {
                                        renderLine([
                                            p - scale, getFrameBottom(),
                                            p - scale, getFrameBottom() + 4.4 * scale,
                                            p + scale, getFrameBottom() + 4.4 * scale,
                                            p + scale, getFrameBottom()
                                        ])
                                    }
                                    {
                                        renderLine([
                                            p, getFrameBottom() + 2.2 * scale,
                                            p, getFrameBottom() + 4.4 * scale
                                        ])
                                    }
                                </React.Fragment>
                            )
                        }
                    )
                }

                return (
                    <React.Fragment>
                        {renderLidOutline()}
                        {renderClosuresOutline()}
                        {renderClosuresIntersectionLines()}
                        {renderAnchors()}
                    </React.Fragment>
                )
            }
            const renderClosuresLongSide = () => {
                const renderLidOutline = () => {
                    return renderLine([
                        getManholeLeft() - getFrameWallWidth(), lidGeometry.top,
                        getManholeLeft() - getFrameWallWidth(), lidGeometry.top + 8 * scale,
                        getManholeRight() + getFrameWallWidth(), lidGeometry.top + 8 * scale,
                        getManholeRight() + getFrameWallWidth(), lidGeometry.top,
                        getManholeLeft() - getFrameWallWidth(), lidGeometry.top
                    ])
                }
                const renderFrameLine = () => {
                    return renderLine([
                        getManholeLeft() - getFrameWallWidth(), lidGeometry.top + 7.5 * scale,
                        getManholeRight() + getFrameWallWidth(), lidGeometry.top + 7.5 * scale
                    ])
                }
                const renderClosureLines = () => {
                    return (
                        <React.Fragment>
                            {renderDashedLine([
                                getManholeLeft(), lidGeometry.top,
                                getManholeLeft(), lidGeometry.top + 8 * scale
                            ])}
                            {renderDashedLine([
                                getManholeRight(), lidGeometry.top,
                                getManholeRight(), lidGeometry.top + 8 * scale
                            ])}
                        </React.Fragment>
                    )
                }
                const renderAnchors = () => {
                    const renderAnchor = () => {
                        return (
                            <React.Fragment>
                                {
                                    renderLine([
                                        0, 0,
                                        2 * scale, 0,
                                        2 * scale, 2 * scale,
                                        3.2 * scale, 4.08 * scale,
                                        2.68 * scale, 4.38 * scale,
                                        1.48 * scale, 2.3 * scale,
                                        1.4 * scale, 2 * scale,
                                        1.4 * scale, 0.6 * scale,
                                        -1.8 * scale, 0.6 * scale,
                                        -1.8 * scale, 2.2 * scale,
                                        -3.8 * scale, 2.2 * scale,
                                        -3.8 * scale, 0
                                    ])
                                }
                                {
                                    renderLine([
                                        1.4 * scale, 2 * scale,
                                        0.2 * scale, 4.08 * scale,
                                        0.72 * scale, 4.38 * scale,
                                        1.7 * scale, 2.52 * scale
                                    ])
                                }
                            </React.Fragment>
                        )
                    }
                    const getFrameBottom = () => lidGeometry.top + 8 * scale

                    return (
                        <React.Fragment>
                            <Group x={getManholeLeft() - getFrameWallWidth()} y={getFrameBottom()} scaleX={-1}>
                                {renderAnchor()}
                            </Group>
                            <Group x={getManholeRight() + getFrameWallWidth()} y={getFrameBottom()}>
                                {renderAnchor()}
                            </Group>
                        </React.Fragment>
                    )
                }

                return (
                    <React.Fragment>
                        {renderLidOutline()}
                        {renderFrameLine()}
                        {renderClosureLines()}
                        {renderAnchors()}
                    </React.Fragment>
                )
            }

            return (
                <React.Fragment>
                    {isClosuresShortSide() && renderClosuresShortSide()}
                    {isClosuresLongSide() && renderClosuresLongSide()}
                </React.Fragment>
            )
        }
        const renderD400 = () => {
            const renderClosuresShortSide = () => {
                const renderLidOutline = () => {
                    return renderLine([
                        getManholeLeft(), lidGeometry.top,
                        getManholeLeft(), lidGeometry.top + 10.5 * scale,
                        getManholeRight(), lidGeometry.top + 10.5 * scale,
                        getManholeRight(), lidGeometry.top,
                        getManholeLeft(), lidGeometry.top
                    ])
                }
                const renderFrameSupportsAlongClosures = () => {
                    return (
                        <React.Fragment>
                            {
                                renderLine([
                                    getManholeLeft(), lidGeometry.top + 4 * scale,
                                    getManholeLeft() - 7.1 * scale, lidGeometry.top + 6.5 * scale,
                                    getManholeLeft() - 7.1 * scale, lidGeometry.top + 10.5 * scale,
                                    getManholeLeft(), lidGeometry.top + 10.5 * scale
                                ])
                            }
                            {
                                renderLine([
                                    getManholeRight(), lidGeometry.top + 4 * scale,
                                    getManholeRight() + 7.1 * scale, lidGeometry.top + 6.5 * scale,
                                    getManholeRight() + 7.1 * scale, lidGeometry.top + 10.5 * scale,
                                    getManholeRight(), lidGeometry.top + 10.5 * scale
                                ])
                            }
                        </React.Fragment>
                    )
                }
                const renderFrameSupportsAcrossClosures = () => {

                    return (
                        <React.Fragment>
                            {
                                manholeGeometry.closureIntersectionPositions()
                                    .map((p, i) => renderLine(
                                        [
                                            p, lidGeometry.top + 4 * scale,
                                            p, lidGeometry.top + 10.5 * scale
                                        ],
                                        'closures_intersection_' + i
                                    ))
                            }
                        </React.Fragment>
                    )
                }
                const renderClosuresBottom = () => {
                    return renderDashedLine([
                        getManholeLeft(), lidGeometry.top + 6 * scale,
                        getManholeRight(), lidGeometry.top + 6 * scale
                    ])
                }
                const renderClosuresIntersections = () => {
                    return (
                        <React.Fragment>
                            {
                                manholeGeometry.closureIntersectionPositions()
                                    .map((p, i) => renderDashedLine(
                                        [p, lidGeometry.top, p, lidGeometry.top + 6 * scale],
                                        'closures_intersection_' + i
                                    ))
                            }
                        </React.Fragment>
                    )
                }
                const renderClosureSupports = () => {
                    const getClosureWidth = () => getClosureSizes(manhole.type).width
                    const getClosuresCount = () => Math.round(manholeGeometry.physicalClearWidth() / getClosureWidth())
                    const getClosuresXPositions = () => {
                        return Array(getClosuresCount()).fill(null).map((_curr, i) => {
                            return i * getClosureWidth() * scale + getManholeLeft()
                        })
                    }

                    return getClosuresXPositions().map(
                        p => {
                            return (
                                <React.Fragment key={'closure_support_x_' + p}>
                                    {
                                        renderDashedLine([
                                            p + scale, lidGeometry.top + 6 * scale,
                                            p + scale, lidGeometry.top + 10.5 * scale
                                        ])
                                    }
                                    {
                                        renderDashedLine([
                                            p + getClosureWidth() * scale - scale, lidGeometry.top + 6 * scale,
                                            p + getClosureWidth() * scale - scale, lidGeometry.top + 10.5 * scale
                                        ])
                                    }
                                    {
                                        renderLine([
                                            p + scale, lidGeometry.top + 10.5 * scale,
                                            p + scale, lidGeometry.top + 14 * scale,
                                            p + getClosureWidth() * scale - scale, lidGeometry.top + 14 * scale,
                                            p + getClosureWidth() * scale - scale, lidGeometry.top + 10.5 * scale,
                                        ])
                                    }
                                </React.Fragment>
                            )
                        }
                    )
                }

                return (
                    <React.Fragment>
                        {renderLidOutline()}
                        {renderFrameSupportsAlongClosures()}
                        {renderFrameSupportsAcrossClosures()}
                        {renderClosuresBottom()}
                        {renderClosureSupports()}
                        {renderClosuresIntersections()}
                    </React.Fragment>
                )
            }
            const renderClosuresLongSide = () => {
                const renderFrameAndClosuresOutline = () => {
                    return renderLine([
                        getManholeLeft(), lidGeometry.top,
                        getManholeLeft(), lidGeometry.top + 4 * scale,
                        manholeGeometry.clearLeft() - 9.1 * scale, lidGeometry.top + 6.5 * scale,
                        manholeGeometry.clearLeft() - 9.1 * scale, lidGeometry.top + 10.5 * scale,
                        manholeGeometry.clearRight() + 9.1 * scale, lidGeometry.top + 10.5 * scale,
                        manholeGeometry.clearRight() + 9.1 * scale, lidGeometry.top + 6.5 * scale,
                        getManholeRight(), lidGeometry.top + 4 * scale,
                        getManholeRight(), lidGeometry.top,
                        getManholeLeft(), lidGeometry.top
                    ])
                }
                const renderClosuresHiddenSideAndBottomLines = () => {
                    return renderDashedLine([
                        getManholeLeft(), lidGeometry.top + 4 * scale,
                        getManholeLeft(), lidGeometry.top + 6 * scale,
                        getManholeRight(), lidGeometry.top + 6 * scale,
                        getManholeRight(), lidGeometry.top + 4 * scale
                    ])
                }
                const renderClosuresSupport = () => {
                    const getDashedLineRatio = () => 4.5 / 8

                    return (
                        <React.Fragment>
                            {
                                renderDashedLine([
                                    manholeGeometry.clearLeft(),
                                    lidGeometry.top + 6 * scale,
                                    manholeGeometry.clearLeft() + 19.9 * getDashedLineRatio() * scale,
                                    lidGeometry.top + 10.5 * scale
                                ])
                            }
                            {
                                renderDashedLine([
                                    manholeGeometry.clearRight(),
                                    lidGeometry.top + 6 * scale,
                                    manholeGeometry.clearRight() - 19.9 * getDashedLineRatio() * scale,
                                    lidGeometry.top + 10.5 * scale
                                ])
                            }
                            {
                                renderLine([
                                    manholeGeometry.clearLeft() + 19.9 * getDashedLineRatio() * scale,
                                    lidGeometry.top + 10.5 * scale,
                                    manholeGeometry.clearLeft() + 19.9 * scale,
                                    lidGeometry.top + 14 * scale,
                                    manholeGeometry.clearRight() - 19.9 * scale,
                                    lidGeometry.top + 14 * scale,
                                    manholeGeometry.clearRight() - 19.9 * getDashedLineRatio() * scale,
                                    lidGeometry.top + 10.5 * scale
                                ])
                            }

                        </React.Fragment>
                    )
                }
                const renderFrameSupportsAlongClosure = () => {
                    const getSupportDistance = () => manholeGeometry.physicalClearWidth() / 5
                    const getSupportPositions = () => {
                        return Array(6).fill(null).map((_curr, i) => {
                            return i * getSupportDistance() * scale + manholeGeometry.clearLeft()
                        })
                    }

                    return getSupportPositions().map(
                        (p, i) => {
                            return (
                                <React.Fragment key={'across_support_' + i}>
                                    {
                                        renderLine([
                                            p, lidGeometry.top + 4 * scale,
                                            p, lidGeometry.top + 10.5 * scale
                                        ])
                                    }
                                </React.Fragment>
                            )
                        }
                    )
                }

                return (
                    <React.Fragment>
                        {renderFrameAndClosuresOutline()}
                        {renderClosuresHiddenSideAndBottomLines()}
                        {renderClosuresSupport()}
                        {renderFrameSupportsAlongClosure()}
                    </React.Fragment>
                )
            }

            return (
                <React.Fragment>
                    {isClosuresShortSide() && renderClosuresShortSide()}
                    {isClosuresLongSide() && renderClosuresLongSide()}
                </React.Fragment>
            )
        }
        const renderE600 = () => {
            const renderClosuresShortSide = () => {
                const renderLidOutline = () => {
                    return renderLine([
                        getManholeLeft(), lidGeometry.top,
                        getManholeLeft(), lidGeometry.top + 15 * scale,
                        getManholeRight(), lidGeometry.top + 15 * scale,
                        getManholeRight(), lidGeometry.top,
                        getManholeLeft(), lidGeometry.top
                    ])
                }
                const renderFrameSupportsAlongClosures = () => {
                    return (
                        <React.Fragment>
                            {
                                renderLine([
                                    getManholeLeft(), lidGeometry.top + 6 * scale,
                                    getManholeLeft() - 7.1 * scale, lidGeometry.top + 11 * scale,
                                    getManholeLeft() - 7.1 * scale, lidGeometry.top + 15 * scale,
                                    getManholeLeft(), lidGeometry.top + 15 * scale
                                ])
                            }
                            {
                                renderLine([
                                    getManholeRight(), lidGeometry.top + 6 * scale,
                                    getManholeRight() + 7.1 * scale, lidGeometry.top + 11 * scale,
                                    getManholeRight() + 7.1 * scale, lidGeometry.top + 15 * scale,
                                    getManholeRight(), lidGeometry.top + 15 * scale
                                ])
                            }
                        </React.Fragment>
                    )
                }
                const renderFrameSupportsAcrossClosures = () => {
                    return (
                        <React.Fragment>
                            {
                                manholeGeometry.closureIntersectionPositions()
                                    .map((p, i) => renderLine(
                                        [
                                            p, lidGeometry.top + 6 * scale,
                                            p, lidGeometry.top + 15 * scale
                                        ],
                                        'closures_intersection_' + i
                                    ))
                            }
                        </React.Fragment>
                    )
                }
                const renderClosuresBottom = () => {
                    return renderDashedLine([
                        getManholeLeft(), lidGeometry.top + 8 * scale,
                        getManholeRight(), lidGeometry.top + 8 * scale
                    ])
                }
                const renderClosuresIntersections = () => {
                    return (
                        <React.Fragment>
                            {
                                manholeGeometry.closureIntersectionPositions()
                                    .map((p, i) => renderDashedLine(
                                        [
                                            p, lidGeometry.top, p,
                                            lidGeometry.top + 8 * scale
                                        ],
                                        'closures_intersection_' + i
                                    ))
                            }
                        </React.Fragment>
                    )
                }
                const renderClosureSupports = () => {
                    const getClosureWidth = () => getClosureSizes(manhole.type).width
                    const getClosuresCount = () => Math.round(manholeGeometry.physicalClearWidth() / getClosureWidth())
                    const getClosuresXPositions = () => {
                        return Array(getClosuresCount()).fill(null).map((_curr, i) => {
                            return i * getClosureWidth() * scale + getManholeLeft()
                        })
                    }

                    return getClosuresXPositions().map(
                        (p, i) => {
                            return renderDashedLine([
                                    p + scale, lidGeometry.top + 8 * scale,
                                    p + scale, lidGeometry.top + 14 * scale,
                                    p + getClosureWidth() * scale - scale, lidGeometry.top + 14 * scale,
                                    p + getClosureWidth() * scale - scale, lidGeometry.top + 8 * scale,
                                ],
                                'support_' + i
                            )

                        }
                    )
                }

                return (
                    <React.Fragment>
                        {renderLidOutline()}
                        {renderFrameSupportsAlongClosures()}
                        {renderFrameSupportsAcrossClosures()}
                        {renderClosuresBottom()}
                        {renderClosureSupports()}
                        {renderClosuresIntersections()}
                    </React.Fragment>
                )
            }
            const renderClosuresLongSide = () => {
                const renderFrameAndClosuresOutline = () => {
                    return renderLine([
                        getManholeLeft(), lidGeometry.top,
                        getManholeLeft(), lidGeometry.top + 6 * scale,
                        manholeGeometry.clearLeft() - 9.1 * scale, lidGeometry.top + 11 * scale,
                        manholeGeometry.clearLeft() - 9.1 * scale, lidGeometry.top + 15 * scale,
                        manholeGeometry.clearRight() + 9.1 * scale, lidGeometry.top + 15 * scale,
                        manholeGeometry.clearRight() + 9.1 * scale, lidGeometry.top + 11 * scale,
                        getManholeRight(), lidGeometry.top + 6 * scale,
                        getManholeRight(), lidGeometry.top,
                        getManholeLeft(), lidGeometry.top
                    ])
                }
                const renderClosuresHiddenSideAndBottomLines = () => {
                    return renderDashedLine([
                        getManholeLeft(), lidGeometry.top + 6 * scale,
                        getManholeLeft(), lidGeometry.top + 8 * scale,
                        getManholeRight(), lidGeometry.top + 8 * scale,
                        getManholeRight(), lidGeometry.top + 6 * scale
                    ])
                }
                const renderClosuresSupport = () => {
                    return (
                        <React.Fragment>
                            {
                                renderDashedLine([
                                    manholeGeometry.clearLeft(), lidGeometry.top + 8 * scale,
                                    manholeGeometry.clearLeft() + 19.9 * scale, lidGeometry.top + 14 * scale,
                                    manholeGeometry.clearRight() - 19.9 * scale, lidGeometry.top + 14 * scale,
                                    manholeGeometry.clearRight(), lidGeometry.top + 8 * scale,
                                ])
                            }

                        </React.Fragment>
                    )
                }
                const renderFrameSupportsAlongClosure = () => {
                    const getSupportDistance = () => manholeGeometry.physicalClearWidth() / 5
                    const getSupportPositions = () => {
                        return Array(6).fill(null).map((_curr, i) => {
                            return i * getSupportDistance() * scale + manholeGeometry.clearLeft()
                        })
                    }

                    return getSupportPositions().map(
                        (p, i) => {
                            return (
                                <React.Fragment key={'across_support_' + i}>
                                    {
                                        renderLine([
                                            p, lidGeometry.top + 6 * scale,
                                            p, lidGeometry.top + 15 * scale
                                        ])
                                    }
                                </React.Fragment>
                            )
                        }
                    )
                }

                return (
                    <React.Fragment>
                        {renderFrameAndClosuresOutline()}
                        {renderClosuresHiddenSideAndBottomLines()}
                        {renderClosuresSupport()}
                        {renderFrameSupportsAlongClosure()}
                    </React.Fragment>
                )
            }

            return (
                <React.Fragment>
                    {isClosuresShortSide() && renderClosuresShortSide()}
                    {isClosuresLongSide() && renderClosuresLongSide()}
                </React.Fragment>
            )
        }

        return (
            <React.Fragment>
                {plan.lid.type === 'A15' && renderA15()}
                {plan.lid.type === 'B125' && renderB125()}
                {plan.lid.type === 'D400' && renderD400()}
                {plan.lid.type === 'E600' && renderE600()}
            </React.Fragment>
        )
    }

    return (
        <React.Fragment>
            {plan.lid.collar !== 'without' && renderWithCollar()}
            {plan.lid.collar === 'without' && renderWithoutCollar()}
        </React.Fragment>
    )
}

